"use client"

import { useState } from "react"
import Link from "next/link"
import { useRouter, usePathname } from "next/navigation"
import { Search, Percent, ShoppingCart, Truck, Store, Tag } from "lucide-react"
import { useCart } from "@/lib/context/cart-context"

export default function ShopNavBar() {
  const router = useRouter()
  const pathname = usePathname()
  const { cart, openCart, fulfillmentType, setFulfillmentType } = useCart()
  const [searchQuery, setSearchQuery] = useState("")
  
  const cartCount = cart.items.reduce((sum: number, item: { quantity: number }) => sum + item.quantity, 0)
  
  // Helper to check if a category is active
  const isActiveCategory = (category: string) => {
    return pathname === `/shop/category/${category}`
  }
  
  // Check if on specials page
  const isSpecialsPage = pathname === '/specials' || pathname.startsWith('/specials/')

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (searchQuery.trim()) {
      router.push(`/shop/search?search=${encodeURIComponent(searchQuery.trim())}`)
      setSearchQuery("")
    }
  }

  return (
    <div className="sticky top-0 z-40 bg-white border-b border-gray-200 shadow-sm">
      <div className="container mx-auto px-4">
        {/* Mobile Layout - shows when hamburger is visible */}
        <div className="xl:hidden py-2 space-y-2">
          {/* Row 1: Search bar + Fulfillment + Cart */}
          <div className="flex items-center gap-2">
            <form onSubmit={handleSearch} className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search products, brands..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2.5 text-sm bg-gray-100 border border-gray-200 rounded-lg focus:outline-none focus:bg-white focus:border-primary_green focus:ring-1 focus:ring-primary_green/20"
              />
            </form>
            {/* Fulfillment Toggle */}
            <div className="flex items-center bg-gray-100 rounded-lg p-0.5 flex-shrink-0">
              <button
                onClick={() => setFulfillmentType('pickup')}
                className={`flex items-center gap-1 px-2 py-1.5 rounded-md text-[10px] font-medium transition-colors ${
                  fulfillmentType === 'pickup' 
                    ? 'bg-white text-gray-900 shadow-sm' 
                    : 'text-gray-500'
                }`}
              >
                <Store className="h-3 w-3" />
              </button>
              <button
                onClick={() => setFulfillmentType('delivery')}
                className={`flex items-center gap-1 px-2 py-1.5 rounded-md text-[10px] font-medium transition-colors ${
                  fulfillmentType === 'delivery' 
                    ? 'bg-white text-gray-900 shadow-sm' 
                    : 'text-gray-500'
                }`}
              >
                <Truck className="h-3 w-3" />
              </button>
            </div>
            <button 
              onClick={openCart}
              className="relative p-2.5 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <ShoppingCart className="h-5 w-5 text-gray-700" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-primary_green text-white text-[10px] font-bold rounded-full h-4 w-4 flex items-center justify-center">
                  {cartCount > 9 ? '9+' : cartCount}
                </span>
              )}
            </button>
          </div>
          
          {/* Row 2: Scrollable category pills */}
          <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide -mx-4 px-4 pb-1" style={{scrollbarWidth: 'none', msOverflowStyle: 'none'}}>
            <Link 
              href="/specials" 
              className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${isSpecialsPage ? 'bg-primary_green text-white' : 'bg-green-100 text-green-700 hover:bg-green-200'}`}
            >
              <Percent className="h-3 w-3" />
              Specials
            </Link>
            <Link 
              href="/shop/brand" 
              className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${pathname.startsWith('/shop/brand') ? 'bg-primary_green text-white' : 'bg-amber-50 text-amber-700 hover:bg-amber-100'}`}
            >
              <Tag className="h-3 w-3" />
              Brands
            </Link>
            <Link href="/shop/category/flower" className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${isActiveCategory('flower') ? 'bg-primary_green text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
              Flower
            </Link>
            <Link href="/shop/category/pre-rolls" className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${isActiveCategory('pre-rolls') ? 'bg-primary_green text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
              Pre-Rolls
            </Link>
            <Link href="/shop/category/vaporizers" className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${isActiveCategory('vaporizers') ? 'bg-primary_green text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
              Vapes
            </Link>
            <Link href="/shop/category/edibles" className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${isActiveCategory('edibles') ? 'bg-primary_green text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
              Edibles
            </Link>
            <Link href="/shop/category/concentrates" className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${isActiveCategory('concentrates') ? 'bg-primary_green text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
              Concentrates
            </Link>
            <Link href="/shop/category/tinctures" className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${isActiveCategory('tinctures') ? 'bg-primary_green text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
              Tinctures
            </Link>
            <Link href="/shop/category" className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${pathname === '/shop/category' ? 'bg-primary_green text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
              More
            </Link>
          </div>
        </div>
        
        {/* Desktop Layout - Single Row (synced with hamburger breakpoint) */}
        <div className="hidden xl:flex items-center h-12 gap-2">
          {/* Specials */}
          <Link 
            href="/specials" 
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-semibold whitespace-nowrap transition-colors ${isSpecialsPage ? 'bg-primary_green text-white' : 'bg-green-50 text-green-700 hover:bg-green-100'}`}
          >
            <Percent className="h-4 w-4" />
            Specials
          </Link>
          
          {/* Categories */}
          <Link href="/shop/category/flower" className={`px-2 py-1.5 text-sm font-medium whitespace-nowrap transition-colors ${isActiveCategory('flower') ? 'text-primary_green' : 'text-gray-700 hover:text-gray-900'}`}>
            Flower
          </Link>
          <Link href="/shop/category/pre-rolls" className={`px-2 py-1.5 text-sm font-medium whitespace-nowrap transition-colors ${isActiveCategory('pre-rolls') ? 'text-primary_green' : 'text-gray-700 hover:text-gray-900'}`}>
            Pre-Rolls
          </Link>
          <Link href="/shop/category/vaporizers" className={`px-2 py-1.5 text-sm font-medium whitespace-nowrap transition-colors ${isActiveCategory('vaporizers') ? 'text-primary_green' : 'text-gray-700 hover:text-gray-900'}`}>
            Vapes
          </Link>
          <Link href="/shop/category/edibles" className={`px-2 py-1.5 text-sm font-medium whitespace-nowrap transition-colors ${isActiveCategory('edibles') ? 'text-primary_green' : 'text-gray-700 hover:text-gray-900'}`}>
            Edibles
          </Link>
          <Link href="/shop/category/concentrates" className={`px-2 py-1.5 text-sm font-medium whitespace-nowrap transition-colors ${isActiveCategory('concentrates') ? 'text-primary_green' : 'text-gray-700 hover:text-gray-900'}`}>
            Concentrates
          </Link>
          <Link href="/shop/category/tinctures" className={`px-2 py-1.5 text-sm font-medium whitespace-nowrap transition-colors ${isActiveCategory('tinctures') ? 'text-primary_green' : 'text-gray-700 hover:text-gray-900'}`}>
            Tinctures
          </Link>
          <Link href="/shop/category" className={`px-2 py-1.5 text-sm font-medium whitespace-nowrap transition-colors ${pathname === '/shop/category' ? 'text-primary_green' : 'text-gray-500 hover:text-gray-700'}`}>
            All
          </Link>
          
          <div className="h-5 w-px bg-gray-200 mx-1"></div>
          
          {/* Strain Pills - smaller */}
          <Link href="/shop/search?strain=sativa" className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-orange-50 text-orange-600 hover:bg-orange-100 transition-colors">
            Sativa
          </Link>
          <Link href="/shop/search?strain=indica" className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-purple-50 text-purple-600 hover:bg-purple-100 transition-colors">
            Indica
          </Link>
          <Link href="/shop/search?strain=hybrid" className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-50 text-emerald-600 hover:bg-emerald-100 transition-colors">
            Hybrid
          </Link>
          
          {/* Right: Search + Fulfillment + Cart - stays pinned */}
          <div className="flex items-center gap-2 ml-auto flex-shrink-0">
            <form onSubmit={handleSearch} className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-36 lg:w-48 pl-8 pr-3 py-1.5 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:bg-white focus:border-primary_green focus:ring-2 focus:ring-primary_green/20 transition-all"
              />
            </form>
            
            {/* Fulfillment Toggle */}
            <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
              <button
                onClick={() => setFulfillmentType('pickup')}
                className={`flex items-center gap-1 px-2 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  fulfillmentType === 'pickup' 
                    ? 'bg-white text-gray-900 shadow-sm' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <Store className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => setFulfillmentType('delivery')}
                className={`flex items-center gap-1 px-2 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  fulfillmentType === 'delivery' 
                    ? 'bg-white text-gray-900 shadow-sm' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <Truck className="h-3.5 w-3.5" />
              </button>
            </div>
            
            <button 
              onClick={openCart}
              className="relative p-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <ShoppingCart className="h-5 w-5 text-gray-700" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-primary_green text-white text-[10px] font-bold rounded-full h-4 w-4 flex items-center justify-center">
                  {cartCount > 9 ? '9+' : cartCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
